'use client';

import React, { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { loadSessionContext, hasStoredSession, refreshSessionPermissions } from '@/services/auth.service';
import { ensureMyEmployee, getMyEmployee } from '@/services/employees.service';
import { AuthApiError, onAccessTokenRefreshed } from '@/lib/api/auth-client';
import { markSessionEnded } from '@/lib/auth/session-end';

export default function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const isInitializing = useAuthStore((s) => s.isInitializing);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUserFromToken = useAuthStore((s) => s.setUserFromToken);
  const patchUserProfileMedia = useAuthStore((s) => s.patchUserProfileMedia);
  const setInitializing = useAuthStore((s) => s.setInitializing);
  const logout = useAuthStore((s) => s.logout);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    return onAccessTokenRefreshed(() => {
      void refreshSessionPermissions();
    });
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!useAuthStore.getState().isAuthenticated) return;
      void refreshSessionPermissions();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!hasStoredSession()) {
        setInitializing(false);
        setReady(true);
        return;
      }

      setInitializing(true);
      try {
        const { profile, products, rolePreview } = await loadSessionContext();
        if (cancelled) return;
        setUserFromToken({
          id: profile.sub ?? profile.id,
          email: profile.email,
          firstName: profile.firstName ?? '',
          lastName: profile.lastName ?? '',
          orgId: profile.orgId,
          role: profile.role,
          isSuperAdmin: profile.isSuperAdmin,
          products,
          profileImageUrl: profile.profileImageUrl,
          coverImageUrl: profile.coverImageUrl,
        });
        if (rolePreview) {
          useAuthStore.getState().setRolePreview({
            roleId: rolePreview.orgRoleId,
            roleName: rolePreview.roleName,
            levelId: rolePreview.orgRoleLevelId,
            levelName: rolePreview.levelName,
            permissions: rolePreview.permissions,
            roleCode: rolePreview.roleCode,
          });
        } else {
          useAuthStore.getState().clearRolePreview();
        }

        try {
          let employee = await getMyEmployee();
          // Org-admin after Hub enable-login often has no HRM employee row yet.
          if (!employee && (profile.role === 'org_admin' || profile.isSuperAdmin)) {
            try {
              employee = await ensureMyEmployee({
                firstName: profile.firstName || undefined,
                lastName: profile.lastName || undefined,
                designation: 'Administrator',
              });
            } catch {
              /* needs workspace setup (department) — Punch Clock CTA can retry */
            }
          }
          if (employee?.profilePhotoUrl) {
            patchUserProfileMedia({ profileImageUrl: employee.profilePhotoUrl });
          }
        } catch {
          /* optional — auth /me URLs are enough */
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof AuthApiError && (err.status === 401 || err.status === 403)) {
          markSessionEnded('session-ended');
          logout();
        } else {
          setInitializing(false);
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, [setUserFromToken, patchUserProfileMedia, setInitializing, logout]);

  if (!ready || (isInitializing && !isAuthenticated)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[var(--hrm-primary)] border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600 dark:text-slate-400 text-sm">Loading your session…</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
